import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useCMSContent, getSectionByType } from '../hooks/useCMSContent';
import axios from 'axios';
import { brandTitle } from '../seo/seoCore';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const FAQPage = () => {
  const { sections, seo } = useCMSContent('faq');
  const heroSection = getSectionByType(sections, 'hero');
  const ctaSection = getSectionByType(sections, 'cta');

  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (seo?.title) document.title = brandTitle(seo.title);
  }, [seo]);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const response = await axios.get(`${API_URL}/faq`);
        setFaqs(response.data);
      } catch (error) {
        console.error('Failed to fetch FAQs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFAQs();
  }, []);

  return (
    <Layout>
      {/* Hero - CMS Driven */}
      <section className="pt-32 pb-20 bg-neutral-50">
        <div className="container-xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="label-overline text-amber-700">{heroSection?.subtitle || 'Support'}</span>
            <h1 className="mt-4 text-neutral-900">{heroSection?.title || 'Frequently Asked Questions'}</h1>
            <p className="mt-6 text-xl text-neutral-600 leading-relaxed">
              {heroSection?.content || 'Find answers to common questions about our products, services, and ordering process.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="section-padding">
        <div className="container-lg">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-neutral-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border border-neutral-200 px-6">
                  <AccordionTrigger className="text-left hover:no-underline py-6">
                    <span className="text-lg font-medium pr-8">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-neutral-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>

      {/* Contact CTA - CMS Driven */}
      <section className="section-padding bg-neutral-50">
        <div className="container-xl text-center">
          <h2 className="mb-6">{ctaSection?.title || 'Still Have Questions?'}</h2>
          <p className="text-neutral-600 max-w-2xl mx-auto mb-8">
            {ctaSection?.content || "Can't find what you're looking for? Our team is here to help. Contact us for personalized assistance with your window treatment project."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="btn-primary inline-flex items-center justify-center">Contact Us</a>
            <a href="tel:2122090654" className="btn-secondary inline-flex items-center justify-center">(212) 209-0654</a>
          </div>
        </div>
      </section>
    </Layout>
  );
};
